import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Shield, AlertTriangle, Activity, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SecurityDashboard = () => {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [suspiciousIPs, setSuspiciousIPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchSecurityData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [statsRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/security-stats`, { headers }),
        axios.get(`${API}/admin/audit-logs?limit=20`, { headers })
      ]);

      setStats(statsRes.data.stats);
      setSuspiciousIPs(statsRes.data.suspicious_ips);
      setLogs(logsRes.data.logs);

      toast({
        title: "Security data loaded",
        description: `${logsRes.data.logs.length} recent events`,
      });
    } catch (error) {
      console.error('Error fetching security data:', error);
      toast({
        title: "Error",
        description: "Failed to load security data",
        variant: "destructive"
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSecurityData();
  }, []);

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'login_attempt':
        return '🔐';
      case 'data_access':
        return '📊';
      case 'logout':
        return '🚪';
      default:
        return '📝';
    }
  };

  const getEventColor = (success) => {
    return success ? 'text-green-600' : 'text-red-600';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-black flex items-center gap-2">
              <Shield className="h-8 w-8" />
              Security Dashboard
            </h1>
            <p className="text-gray-600">Monitor admin activity and security events</p>
          </div>
          <Button
            onClick={fetchSecurityData}
            disabled={loading}
            className="bg-emerald-700 hover:bg-emerald-800"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Total Events
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-4xl font-bold">{stats.total_events}</p>
                <p className="text-sm text-gray-600 mt-2">All time</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  Failed Logins
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-4xl font-bold text-orange-600">{stats.failed_logins_24h}</p>
                <p className="text-sm text-gray-600 mt-2">Last 24 hours</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  🔐 Login Attempts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-4xl font-bold">{stats.event_counts.login_attempt || 0}</p>
                <p className="text-sm text-gray-600 mt-2">Total attempts</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Suspicious IPs Alert */}
        {suspiciousIPs.length > 0 && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="text-red-700 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Suspicious Activity Detected
              </CardTitle>
              <CardDescription className="text-red-600">
                Multiple failed login attempts from the following IPs
              </CardDescription>
            </CardHeader>
            <CardContent>
              {suspiciousIPs.map((ip, index) => (
                <div key={index} className="bg-white p-3 rounded mb-2">
                  <p className="font-mono font-bold">{ip._id}</p>
                  <p className="text-sm text-gray-600">
                    {ip.attempts} failed attempts • Tried usernames: {ip.usernames_tried.join(', ')}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Audit Logs */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity Log</CardTitle>
            <CardDescription>Last 20 security events</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-gray-500">Loading...</p>
            ) : logs.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No audit logs yet</p>
            ) : (
              <div className="space-y-2">
                {logs.map((log, index) => (
                  <div
                    key={index}
                    className="border-b last:border-0 pb-3 mb-3 last:pb-0 last:mb-0"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">{getEventIcon(log.event_type)}</span>
                        <div>
                          <p className="font-medium">
                            <span className={getEventColor(log.success)}>
                              {log.success ? '✓' : '✗'}
                            </span>
                            {' '}
                            {log.event_type.replace('_', ' ').toUpperCase()}
                          </p>
                          <p className="text-sm text-gray-600">
                            User: <span className="font-mono">{log.username || 'anonymous'}</span>
                            {' '} • IP: <span className="font-mono">{log.ip_address}</span>
                          </p>
                          {log.details && (
                            <p className="text-xs text-gray-500 mt-1">
                              {log.details.resource && `Resource: ${log.details.resource}`}
                              {log.details.action && ` • Action: ${log.details.action}`}
                              {log.details.record_count !== undefined && ` • Records: ${log.details.record_count}`}
                              {log.details.failure_reason && ` • Reason: ${log.details.failure_reason}`}
                            </p>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SecurityDashboard;
